/**
 * Version control as data (plan 7.5), shared by the web and native panels:
 * the compact control (Previous version, "Version 3 of 5", Next version),
 * the rows of the version list, the older-version banner and the card's
 * "Version N". Versions come newest first from `GET versions`; the numbers
 * never repeat but pruning can leave gaps, so "of 5" is the head's number.
 */
import type { ArtifactVersionSummary } from '@tabbio/types';

export type VersionRef = { id: string; number: number };

export type VersionControlModel = {
  /** The version on screen. */
  current: VersionRef | null;
  /** The head's number ("of 5"). */
  total: number;
  isLatest: boolean;
  /** The next older version that can be shown (ready), or null. */
  previous: VersionRef | null;
  /** The next newer version that can be shown, or null. */
  next: VersionRef | null;
  /** Worth showing: more than one version exists. */
  visible: boolean;
};

function ref(version: Pick<ArtifactVersionSummary, 'id' | 'number'>): VersionRef {
  return { id: version.id, number: version.number };
}

/**
 * The control for the version on screen: `viewingId` null means the head.
 * Previous and Next skip versions that are not ready (a pending or failed
 * image edit has nothing to show).
 */
export function versionControlModel(input: {
  versions: readonly ArtifactVersionSummary[];
  viewingId: string | null;
  head: VersionRef | null;
  /** The head's number from the artifact (`versionCount`), when the list is not loaded yet. */
  versionCount?: number | null;
}): VersionControlModel {
  const sorted = [...input.versions].sort((a, b) => b.number - a.number);
  const headRow = sorted.find((version) => version.isCurrent) ?? null;
  const head = input.head ?? (headRow ? ref(headRow) : null);
  const total = Math.max(head?.number ?? 0, input.versionCount ?? 0, sorted[0]?.number ?? 0);
  const viewing = input.viewingId ? sorted.find((version) => version.id === input.viewingId) ?? null : null;
  const current = viewing ? ref(viewing) : head;
  const isLatest = !input.viewingId || (head !== null && input.viewingId === head.id);
  const at = current ? current.number : total;
  const readyRows = sorted.filter((version) => version.status === 'ready');
  const olderRow = readyRows.find((version) => version.number < at) ?? null;
  const newerRow = [...readyRows].reverse().find((version) => version.number > at) ?? null;
  return {
    current,
    total,
    isLatest,
    previous: olderRow ? ref(olderRow) : null,
    next: newerRow ? ref(newerRow) : null,
    visible: total > 1,
  };
}

/** What the panel asks for when the reader moves: null is the head. */
export function viewingIdFor(target: VersionRef | null, head: VersionRef | null): string | null {
  if (!target) return null;
  return head && target.id === head.id ? null : target.id;
}

export type VersionByline = { key: string; params?: Record<string, string | number> };

/** "Edited by you", "Made by Tabbio" or "Restored from version 2". */
export function versionByline(version: Pick<ArtifactVersionSummary, 'source' | 'restoredFrom'>): VersionByline {
  switch (version.source) {
    case 'user':
      return { key: 'chat.artifacts.versions.byYou' };
    case 'restore':
      return version.restoredFrom
        ? { key: 'chat.artifacts.versions.restoredFrom', params: { number: version.restoredFrom.number } }
        : { key: 'chat.artifacts.versions.restoredFromUnknown' };
    default:
      return { key: 'chat.artifacts.versions.byTabbio' };
  }
}

export type VersionRowModel = {
  id: string;
  number: number;
  byline: VersionByline;
  /** The change summary, when the version has one. */
  summary: string | null;
  /** The live site shows this version (the globe mark). */
  live: boolean;
  isCurrent: boolean;
  /** On screen now. */
  selected: boolean;
  status: ArtifactVersionSummary['status'];
  /** A failed version's reason (an image edit the model refused). */
  error: string | null;
  createdAt: string;
};

export function versionRows(
  versions: readonly ArtifactVersionSummary[],
  viewingId: string | null,
): VersionRowModel[] {
  return [...versions]
    .sort((a, b) => b.number - a.number)
    .map((version) => ({
      id: version.id,
      number: version.number,
      byline: versionByline(version),
      summary: version.summary?.trim() || null,
      live: Boolean(version.publishedAs?.live),
      isCurrent: version.isCurrent,
      selected: viewingId ? version.id === viewingId : version.isCurrent,
      status: version.status,
      error: version.status === 'failed' ? version.error?.message ?? null : null,
      createdAt: version.createdAt,
    }));
}

/**
 * "3 minutes ago", "yesterday" in the reader's locale; a short date when
 * the runtime has no `Intl.RelativeTimeFormat`.
 */
export function relativeVersionTime(iso: string, locale: string, now = Date.now()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = date.getTime() - now;
  const abs = Math.abs(diff);
  try {
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['day', 86_400_000],
      ['hour', 3_600_000],
      ['minute', 60_000],
      ['second', 1_000],
    ];
    for (const [unit, ms] of units) {
      if (abs >= ms || unit === 'second') {
        if (unit === 'day' && abs >= 7 * 86_400_000) break;
        return format.format(Math.round(diff / ms), unit);
      }
    }
  } catch {
    // Fall through to a date.
  }
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
  } catch {
    return date.toDateString();
  }
}

/** The card's "Version N" (plan 7.5): only when N is greater than 1. */
export function cardVersionNumber(number: number | null | undefined): number | null {
  return typeof number === 'number' && number > 1 ? number : null;
}
